import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CalendarDays, Clock, Check, Shield, ArrowLeft, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getNextSunday() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 7 : 7 - day;
  const d = new Date(now);
  d.setDate(now.getDate() + diff);
  return d;
}

function getNextSaturday() {
  const sun = getNextSunday();
  const sat = new Date(sun);
  sat.setDate(sun.getDate() - 1);
  return sat;
}

function formatDate(d) {
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const day = d.getDate();
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

const whyAttend = [
  'You will learn Techniques and Practices to Heal Deep-Rooted Childhood Trauma',
  'Learn Strategies and Techniques to reprogram your subconscious Beliefs',
  'Manifest your dream life AND make 2026 your best year',
  'Remove Money blocks and attract wealth',
  'Get Rid of Anxious thoughts and Overthinking in Relationships',
];

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const session = searchParams.get('session') || 'morning';

  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [pricing, setPricing] = useState({ price_inr: 499, price_usd: 5.99 });

  const nextSunday = useMemo(() => getNextSunday(), []);
  const nextSaturday = useMemo(() => getNextSaturday(), []);
  const sessionDate = session === 'morning' ? formatDate(nextSunday) : formatDate(nextSaturday);
  const sessionTime = session === 'morning' ? '9:30 AM to 12:30 PM IST' : '5:30 PM to 8:30 PM IST';

  // Fetch pricing
  React.useEffect(() => {
    axios.get(`${API}/masterclass/pricing`).then(res => setPricing(res.data)).catch(() => {});
  }, []);

  // Check for Stripe success redirect
  React.useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      setPaymentSuccess(true);
    }
  }, [searchParams]);

  const isFormValid = form.name && form.email && form.phone;

  const handleRazorpayPayment = async () => {
    if (!isFormValid) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/payments/razorpay/masterclass`, {
        origin_url: window.location.origin
      });
      if (data.order_id && data.key_id) {
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: 'Manifest with Dr. Dinesh',
          description: 'Heal And Manifest Masterclass',
          order_id: data.order_id,
          handler: async function (response) {
            try {
              await axios.post(`${API}/payments/razorpay/masterclass/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              await axios.post(`${API}/masterclass/register`, form).catch(() => {});
              setPaymentSuccess(true);
            } catch {
              alert('Payment verification failed. Please contact support.');
            }
          },
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: '#4338ca' }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch {
      alert('Payment failed. Please try again.');
    }
    setProcessing(false);
  };

  const handleStripePayment = async () => {
    if (!isFormValid) return;
    setProcessing(true);
    try {
      // Register first
      await axios.post(`${API}/masterclass/register`, form).catch(() => {});
      const { data } = await axios.post(`${API}/payments/stripe/masterclass`, {
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Payment failed. Please try again.');
    }
    setProcessing(false);
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full bg-white rounded-3xl p-10 shadow-xl text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="font-serif text-3xl font-bold text-slate-900 mb-3" data-testid="checkout-success-title">Registration Successful!</h2>
          <p className="text-slate-600 mb-2">You are now registered for the <strong>Heal & Manifest Masterclass</strong>.</p>
          <p className="text-slate-500 text-sm mb-8">Check your email for the Zoom link and masterclass details.</p>
          <a href="https://chat.whatsapp.com/LbOL5Nm5UxZ0k8O7aokKm2" target="_blank" rel="noopener noreferrer">
            <Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-base font-bold gap-2 shadow-lg" data-testid="checkout-whatsapp-btn">
              <MessageCircle className="w-5 h-5" /> Join WhatsApp Group for Updates
            </Button>
          </a>
          <p className="text-slate-400 text-xs mt-3">Join the group to receive the Zoom link and bonuses</p>
          <button onClick={() => navigate('/')} className="mt-6 text-indigo-600 text-sm font-semibold hover:underline">
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium" data-testid="checkout-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span className="text-xs sm:text-sm text-slate-500">Secure Checkout</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">

          {/* ═══════ LEFT COLUMN: Product Details ═══════ */}
          <div className="lg:col-span-3" data-testid="checkout-product-details">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {/* Title & Price */}
              <h1 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-1" data-testid="checkout-title">
                Heal & Manifest Masterclass — {session === 'morning' ? 'Morning' : 'Evening'}
              </h1>
              <p className="text-slate-500 text-sm mb-4">By Dr. Dinesh Mungath</p>

              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-slate-400 line-through text-lg">&#8377;2,999</span>
                <span className="text-slate-900 font-bold text-4xl font-serif" data-testid="checkout-price">&#8377;{pricing.price_inr}</span>
                <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">83% OFF</span>
              </div>

              {/* Banner with Dr. Dinesh Photo */}
              <div className="relative rounded-2xl overflow-hidden mb-8 shadow-lg" data-testid="checkout-banner">
                <div className="bg-gradient-to-r from-amber-700 via-amber-600 to-amber-500 p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row gap-6 items-center">
                    {/* Left: Text */}
                    <div className="flex-1 text-white">
                      <h2 className="font-serif text-xl sm:text-2xl font-bold mb-4 leading-tight">
                        Heal & Manifest<br />Masterclass
                      </h2>
                      <ul className="space-y-2">
                        {['Rewire Your Subconscious Beliefs', 'Manifest Your Dream Life', 'Heal Your Childhood Trauma', 'Stop Procrastinating & Become an Achiever'].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                            <span className="text-amber-300 mt-0.5">&#9670;</span>{item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-white/60 mt-4 font-medium">Dr. Dinesh Mungath</p>
                    </div>
                    {/* Right: Coach Photo */}
                    <div className="flex-shrink-0">
                      <img
                        src="https://customer-assets.emergentagent.com/job_manifest-dinesh/artifacts/bca2afwc_12.png"
                        alt="Dr. Dinesh Mungath"
                        className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl object-cover object-top shadow-xl border-4 border-white/20"
                        data-testid="checkout-coach-photo"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-200 flex-1">
                  <CalendarDays className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase">Date</p>
                    <p className="text-slate-900 font-bold text-sm" data-testid="checkout-date">{sessionDate}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-200 flex-1">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase">Time</p>
                    <p className="text-slate-900 font-bold text-sm" data-testid="checkout-time">{sessionTime}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className="text-slate-600 text-base italic leading-relaxed">
                  Unlock the Power Within: Learn how to heal your past & Manifest your Dream Life
                </p>
              </div>

              {/* Why Attend */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="checkout-why-attend">
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-5">Why attend this workshop:</h3>
                <ol className="space-y-4">
                  {whyAttend.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">{i + 1}</span>
                      <p className="text-slate-700 text-sm sm:text-base leading-relaxed">{item}</p>
                    </li>
                  ))}
                </ol>
                <p className="mt-6 text-sm text-slate-500 font-semibold border-t border-slate-100 pt-4">
                  NOTE: No recordings will be shared.
                </p>
              </div>
            </motion.div>
          </div>

          {/* ═══════ RIGHT COLUMN: Payment Form ═══════ */}
          <div className="lg:col-span-2" data-testid="checkout-payment-section">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg sticky top-20">
                {/* Header */}
                <div className="p-6 sm:p-8 border-b border-slate-100">
                  <h2 className="font-serif text-xl font-bold text-slate-900 mb-1">Payment details</h2>
                  <p className="text-slate-500 text-sm">Complete your purchase by providing your payment details.</p>
                </div>

                {/* Billing Form */}
                <div className="p-6 sm:p-8" data-testid="checkout-billing-form">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">Billing information</h3>

                  <div className="space-y-4 mb-6">
                    <Input
                      placeholder="Name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 text-base"
                      required
                      data-testid="checkout-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 text-base"
                      required
                      data-testid="checkout-email-input"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="h-12 rounded-xl border-slate-200 text-base"
                      required
                      data-testid="checkout-phone-input"
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-6" data-testid="checkout-order-summary">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600 text-sm">Masterclass Registration</span>
                      <span className="text-slate-400 line-through text-sm">&#8377;2,999</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600 text-sm">Discount (83% off)</span>
                      <span className="text-emerald-600 text-sm font-medium">-&#8377;2,500</span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between items-center">
                      <span className="text-slate-900 font-bold">Total</span>
                      <span className="text-slate-900 font-bold text-xl font-serif">&#8377;{pricing.price_inr}</span>
                    </div>
                  </div>

                  {/* Pay Buttons */}
                  <div className="space-y-3">
                    <Button
                      type="button"
                      onClick={handleRazorpayPayment}
                      disabled={processing || !isFormValid}
                      className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white h-14 text-lg font-bold shadow-lg shadow-amber-500/20 transition-all"
                      data-testid="checkout-razorpay-btn"
                    >
                      {processing ? 'Processing...' : `Pay \u20B9${pricing.price_inr} — India (UPI / Card / Netbanking)`}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleStripePayment}
                      disabled={processing || !isFormValid}
                      className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white h-12 text-base font-semibold"
                      data-testid="checkout-stripe-btn"
                    >
                      {processing ? 'Processing...' : `Pay $${pricing.price_usd} USD — International (Stripe)`}
                    </Button>
                  </div>

                  {/* Trust Badges */}
                  <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                      <Shield className="w-3.5 h-3.5" /> Secure Payment
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                      <Check className="w-3.5 h-3.5" /> Instant Access
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </div>
  );
}
